import { motion } from 'framer-motion'

import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { OptionsModalContainer, OptionsModalHeader } from '@apps-common/components/modals/options-modals'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { UnitActivationAction } from '@apps-common/config/unit-activation'
import { useUnitActivationStatusOptions } from '@apps-common/hooks/useUnitActivationStatusOptions'
import { AUTH_CLAIMS, Authorization } from '@apps-common/lib'
import { IActivationServiceRequestModel } from '@apps-common/types/portal/service-request'
import classNames from 'classnames'
import { ActionButton } from '../components/UnitActivationActionButton'
import { UnitActivationStatusPill } from '../components/UnitActivationStatusPill'

const ACTIVATION_OPTIONS_MODAL_ACTION_EXTRA_STYLES =
  'w-full !h-[30px] px-2 gap-[5px] disabled:!cursor-not-allowed disabled:!bg-surface !text-[0.8rem]'

export interface IUnitActivationOptionsModal {
  activation: IActivationServiceRequestModel
}

export const UnitActivationOptionsModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: IUnitActivationOptionsModal
}) => {
  const { activation } = componentProps
  const { status, archived } = activation
  const { isProcessing, showRetry, showActivate, showDelete, showArchive, showEdit, canCancel } = useUnitActivationStatusOptions(
    status,
    archived
  )

  const loading = !activation

  const isAwaiting = loading

  const optionsDisabled = isAwaiting || !showRetry || !showActivate || !showDelete || !showArchive || !showEdit || !canCancel

  const buildOptions = () => (
    <div className='flex flex-col gap-[15px]'>
      <Authorization roles={AUTH_CLAIMS.managementAdmin.roles} scopes={AUTH_CLAIMS.managementAdmin.scopes}>
        <ModalButton
          component={ConfirmModal}
          disabled={!isProcessing}
          classes=''
          componentProps={{
            text: 'Force Cancel',
            description: `Confirm you would like to FORCE cancel this device activation? 
                      
                      Please note that if the activation is in progress, it may still complete the activation and potentially update this activation afterwards. 
                      Please only use this feature if you believe the activation is stuck.`,
            action: ConfirmModalAction.Error
          }}
          confirmCallback={() => onDismiss(JSON.stringify({ action: UnitActivationAction.forceCancel }), ModalDismiss.Confirm)}
        >
          <ActionButton
            type='div'
            action={UnitActivationAction.terminate}
            label='Force Cancel'
            disabled={!isProcessing}
            className={classNames('action-delete', ACTIVATION_OPTIONS_MODAL_ACTION_EXTRA_STYLES)}
          />
        </ModalButton>
      </Authorization>
      <ActionButton
        type='button'
        action={UnitActivationAction.activateNow}
        disabled={(!showActivate && !showRetry) || isAwaiting}
        label={showRetry ? 'Retry now' : 'Activate now'}
        className={classNames('action-activate-now', ACTIVATION_OPTIONS_MODAL_ACTION_EXTRA_STYLES)}
        callback={() => onDismiss(JSON.stringify({ action: UnitActivationAction.activateNow }), ModalDismiss.Confirm)}
      />
      <ActionButton
        type='button'
        action={UnitActivationAction.powerUp}
        disabled={(!showActivate && !showRetry) || isAwaiting}
        label={showRetry ? 'Retry on power up' : 'Activate on power up'}
        className={classNames('action-power-up', ACTIVATION_OPTIONS_MODAL_ACTION_EXTRA_STYLES)}
        callback={() => onDismiss(JSON.stringify({ action: UnitActivationAction.powerUp }), ModalDismiss.Confirm)}
      />

      <div className='border-t border-border w-full mx-auto my-[5px]' />

      <ActionButton
        type='button'
        action={UnitActivationAction.edit}
        disabled={!showEdit || isAwaiting}
        label='Edit'
        className={classNames('action-edit', ACTIVATION_OPTIONS_MODAL_ACTION_EXTRA_STYLES)}
        callback={() => onDismiss(JSON.stringify({ action: UnitActivationAction.edit }), ModalDismiss.Confirm)}
      />
      <ModalButton
        component={ConfirmModal}
        disabled={!showDelete || isAwaiting}
        classes=''
        componentProps={{
          text: 'Delete',
          description: 'Confirm you would like to delete this device activation?',
          action: ConfirmModalAction.Error
        }}
        confirmCallback={() => onDismiss(JSON.stringify({ action: UnitActivationAction.delete }), ModalDismiss.Confirm)}
      >
        <ActionButton
          type='div'
          action={UnitActivationAction.delete}
          disabled={!showDelete || isAwaiting}
          label='Delete'
          className={classNames('action-delete', ACTIVATION_OPTIONS_MODAL_ACTION_EXTRA_STYLES)}
        />
      </ModalButton>
      <ModalButton
        component={ConfirmModal}
        disabled={!showArchive || isAwaiting}
        classes=''
        componentProps={{
          text: 'Archive',
          description: 'Confirm you would like to archive this device activation?',
          action: ConfirmModalAction.Warning
        }}
        confirmCallback={() => onDismiss(JSON.stringify({ action: UnitActivationAction.archive }), ModalDismiss.Confirm)}
      >
        <ActionButton
          type='div'
          action={UnitActivationAction.archive}
          disabled={!showArchive || isAwaiting}
          label='Archive'
          className={classNames('action-archive', ACTIVATION_OPTIONS_MODAL_ACTION_EXTRA_STYLES)}
        />
      </ModalButton>
      <ModalButton
        component={ConfirmModal}
        disabled={!canCancel || loading}
        classes=''
        componentProps={{
          text: 'Cancel',
          description: 'Confirm you would like to cancel this device activation?',
          action: ConfirmModalAction.Warning
        }}
        confirmCallback={() => onDismiss(JSON.stringify({ action: UnitActivationAction.cancel }), ModalDismiss.Confirm)}
      >
        <ActionButton
          type='div'
          action={UnitActivationAction.cancel}
          disabled={!canCancel || loading}
          label='Cancel'
          className={classNames('action-cancel', ACTIVATION_OPTIONS_MODAL_ACTION_EXTRA_STYLES)}
        />
      </ModalButton>

      <div className='border-t border-border w-full mx-auto my-[5px]' />

      <ButtonRoundedSm
        type='button'
        loading={false}
        className={classNames('action-neutral', ACTIVATION_OPTIONS_MODAL_ACTION_EXTRA_STYLES)}
        callback={() => onDismiss(null, ModalDismiss.Cancel)}
        text='Close'
      />
    </div>
  )

  return (
    <OptionsModalContainer onDismiss={onDismiss}>
      <motion.div layout className='bg-surface-2 px-[2rem] py-[1.25rem] rounded-3xl w-full'>
        <div className='max-w-[220px] sm:w-[180px] flex flex-col mx-auto'>
          <OptionsModalHeader
            Icon={commonIcons.srActivations}
            subtitle='Device Activation'
            title='What would you like to do?'
            someOptionsDisabled={optionsDisabled}
            disabledMessage='Current status is limiting available options'
            children={
              <div className='scale-[0.9] bg-surface p-1 rounded-full'>
                <UnitActivationStatusPill status={status} />
              </div>
            }
          />
          {buildOptions()}
        </div>
      </motion.div>
    </OptionsModalContainer>
  )
}
